import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../AuthContext";
import { RefreshCw, Activity, Server } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_STYLES = {
  ok: "bg-emerald-500/12 text-emerald-500",
  error: "bg-rose-500/12 text-rose-500",
  stale: "bg-amber-500/12 text-amber-500",
  warn: "bg-amber-500/12 text-amber-500",
  running: "bg-sky-500/12 text-sky-500",
  pending: "bg-slate-500/12 text-slate-400",
  disabled: "bg-slate-500/10 text-slate-500",
};

const relTime = (iso) => {
  if (!iso) return "never";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return `${Math.round(s)}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${(s / 3600).toFixed(1)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export default function JobHealthBoard({ dm }) {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/system/health`, { headers: authHeader(), params: refresh ? { refresh: true } : {} });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load system health");
    } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const badge = (state) => (
    <span className={`px-2 py-0.5 rounded-full category-tag ${STATE_STYLES[state] || STATE_STYLES.pending}`}>{(state || "n/a").toUpperCase()}</span>
  );

  const jobs = data?.jobs || [];
  const problems = jobs.filter(j => ["error", "stale"].includes(j.state)).length
    + (data?.integrations || []).filter(i => i.state === "error").length;

  return (
    <div data-testid="job-health-board" className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Activity className={`w-4 h-4 ${problems ? "text-rose-500" : "text-emerald-500"}`} />
          <h3 className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>System Health</h3>
          {data && (
            <span data-testid="jhb-problem-count" className={`category-tag px-2 py-0.5 rounded-full ${problems ? "bg-rose-500/12 text-rose-500" : "bg-emerald-500/12 text-emerald-500"}`}>
              {problems ? `${problems} issue${problems > 1 ? "s" : ""}` : "all healthy"}
            </span>
          )}
          {data?.environment && (
            <span data-testid="jhb-env-badge"
              title={data.environment === "preview" ? "Development environment — alerts here do NOT reflect production status" : "Live production environment"}
              className={`category-tag px-2 py-0.5 rounded-full font-semibold ${data.environment === "preview" ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/10 text-emerald-500"}`}>
              {data.environment === "preview" ? "⚠ PREVIEW" : "PRODUCTION"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data?.pods && (
            <span className={`category-tag px-2 py-0.5 rounded-full flex items-center gap-1 ${dm ? "bg-white/5 text-slate-400" : "bg-black/5 text-[#6b7a7d]"}`} title={`current: ${data.pods.current}\nboot: ${data.pods.boot_time}`}>
              <Server className="w-3 h-3" />{(data.pods.seen_last_hour || []).length || 1} pod{((data.pods.seen_last_hour || []).length || 1) > 1 ? "s" : ""}
            </span>
          )}
          <button data-testid="jhb-refresh-btn" onClick={() => load(true)} disabled={loading}
            className={`px-3 py-1.5 rounded-full category-tag flex items-center gap-1.5 disabled:opacity-50 ${dm ? "bg-white/5 text-slate-300" : "bg-black/5 text-[#3c4a4d]"}`}>
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />Refresh
          </button>
        </div>
      </div>

      {/* Integrations */}
      <div data-testid="jhb-integrations" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5">
        {(data?.integrations || []).map(it => (
          <div key={it.name} className={`rounded-xl px-3 py-2.5 ${dm ? "bg-[#141a1b]" : "bg-[#fafbfb]"}`} data-testid={`jhb-integration-${it.name.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between gap-1">
              <span className={`text-xs font-semibold ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>{it.name}</span>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${it.state === "ok" ? "bg-emerald-500" : it.state === "error" ? "bg-rose-500" : "bg-amber-500"}`} />
            </div>
            <p className={`text-[10px] mt-0.5 truncate ${dm ? "text-slate-500" : "text-[#8a999c]"}`} title={it.detail}>{it.detail}</p>
          </div>
        ))}
      </div>

      {/* Jobs board */}
      <div data-testid="jhb-jobs-table" className={`rounded-xl overflow-x-auto ${dm ? "bg-[#141a1b]" : "bg-[#fafbfb]"}`}>
        <table className="w-full text-xs">
          <thead>
            <tr className={`text-left category-tag ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>
              <th className="px-3 py-2">Job</th><th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Last run</th><th className="px-3 py-2">Last OK</th>
              <th className="px-3 py-2">Duration</th><th className="px-3 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => (
              <tr key={j.job} data-testid={`jhb-job-${j.job}`} className={`border-t ${dm ? "border-white/4" : "border-black/4"}`}>
                <td className={`px-3 py-2 font-semibold whitespace-nowrap ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{j.label}</td>
                <td className="px-3 py-2">{badge(j.state)}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} title={j.last_run_at || ""}>{relTime(j.last_run_at)}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${dm ? "text-slate-500" : "text-[#8a999c]"}`} title={j.last_ok_at || ""}>{relTime(j.last_ok_at)}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>{j.duration_ms != null ? `${j.duration_ms} ms` : "—"}</td>
                <td className={`px-3 py-2 max-w-[280px] ${j.error_msg ? "text-rose-500" : dm ? "text-slate-500" : "text-[#8a999c]"}`}>
                  <span className="block truncate font-mono" title={j.error_msg || JSON.stringify(j.counters || {})}>
                    {j.error_msg || (j.counters ? Object.entries(j.counters).filter(([, v]) => v != null).map(([k, v]) => `${k}:${v}`).join(" · ") : (j.interval_min ? `every ${j.interval_min}m` : "scheduled"))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!jobs.length && <p className={`text-sm text-center py-6 ${dm ? "text-slate-600" : "text-[#8a999c]"}`}>{loading ? "Loading…" : "No job data yet"}</p>}
      </div>
    </div>
  );
}
